import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  salesforceAiConnectorName,
  salesforceAiConsumerSecretSecretId,
} from "@p0security/integrations-salesforce-ai-shared/secret-manager";
import type { SalesforceAiComponents } from "@p0security/integrations-salesforce-ai-shared/types";
import {
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  SalesforceAiComponents["org"]
>["secretManager"];

type AwsSecretManagerConfig = Extract<SecretManagerConfig, { type: "aws-sm" }>;
type GcpSecretManagerConfig = Extract<SecretManagerConfig, { type: "gcp-sm" }>;

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-salesforce-ai-gcloud",
  digest:
    "sha-916c31e@sha256:ae1781255a82c1971bed3f45053a0c69389b987d8b9aa24adecb766c04bc7559",
} as const;

const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-salesforce-ai-aws",
  digest:
    "sha-f9923eb@sha256:6ad35d1a3e07773bc6581ee8c8fdc4216ae1f979b06c1f76d7ade1662d5818fd",
} as const;

const awsConsumerSecretSecretId = (org: string) =>
  secretProviderFor("aws").replacer(salesforceAiConsumerSecretSecretId(org));

export const awsStoreConsumerSecret = (
  org: string,
  secretManager: AwsSecretManagerConfig
) =>
  shellLines(
    `printf '%s' "$SF_CONSUMER_SECRET"`,
    "| aws secretsmanager put-secret-value",
    `--secret-id ${awsConsumerSecretSecretId(org)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file:///dev/stdin"
  );

export const awsTf = (
  context: InstallContext,
  org: string,
  secretManager: AwsSecretManagerConfig
) =>
  awsConnectorTf({
    names: { resource: "salesforce", label: "Salesforce" },
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: salesforceAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    installSecret: {
      resource: "consumer_secret",
      itemTfId: tfId(org),
      secretId: awsConsumerSecretSecretId(org),
      label: "consumer secret",
    },
  });

const gcpConsumerSecretSecretId = (org: string) =>
  secretProviderFor("gcp").replacer(salesforceAiConsumerSecretSecretId(org));

export const gcpStoreConsumerSecret = (
  org: string,
  secretManager: GcpSecretManagerConfig
) =>
  shellLines(
    `printf '%s' "$SF_CONSUMER_SECRET"`,
    `| gcloud secrets versions add ${gcpConsumerSecretSecretId(org)}`,
    `--project=${secretManager.install}`,
    "--data-file=-"
  );

export const gcpTf = (
  context: InstallContext,
  org: string,
  secretManager: GcpSecretManagerConfig
) =>
  gcpConnectorTf({
    names: { resource: "salesforce", role: "Salesforce", label: "Salesforce" },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    installSecret: {
      resource: "consumer_secret",
      itemTfId: tfId(org),
      secretId: gcpConsumerSecretSecretId(org),
      label: "consumer secret",
    },
  });
